import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Public } from '@mui/icons-material'
import { Alert, Box, Card, Chip, Stack, TableContainer, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useGetInternalMarketsQuery, usePublishMarketMutation, useSaveMarketMutation } from '../../api/internal-markets'
import { useGetLanguageMapsQuery } from '../../api/language'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { MarketEditDialog } from '../../features/markets/components/MarketEditDialog/MarketEditDialog'
import { buildMarketRows, getUnstoredMarketFields, marketFieldLabelKeys, MarketRow } from '../../features/markets/helpers/marketHelpers'
import { MarketMutation } from '../../features/markets/types/InternalMarket'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = {
  onEdit: (row: MarketRow) => void
  onPublish: (row: MarketRow) => void
}

type DialogState = { open: boolean; row?: MarketRow }

export const MarketsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-markets'))
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()

  const marketsQuery = useGetInternalMarketsQuery()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const [saveMarket, { isLoading: isSaving }] = useSaveMarketMutation()
  const [publishMarket] = usePublishMarketMutation()
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<MarketRow>>()

  const markets = useMemo(() => marketsQuery.data ?? [], [marketsQuery.data])
  const rows = useMemo(() => buildMarketRows(markets, languageMapsQuery.data), [markets, languageMapsQuery.data])
  const isLoading = marketsQuery.isLoading || languageMapsQuery.isLoading
  const isError = marketsQuery.isError || languageMapsQuery.isError

  const tableColumns: GRTableColumn<MarketRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:markets_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.market.name}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.market.name.toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 220 } },
      },
      {
        labelAccessor: t('internal-service-management:markets_iso'),
        accessor: ({ row }) => row.market.iso,
        sortable: true,
        sortAccessor: ({ row }) => row.market.iso.toLowerCase(),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 140 } },
      },
      {
        labelAccessor: t('internal-service-management:markets_status'),
        accessor: ({ row }) =>
          row.market.published ? (
            <Chip size="small" color="success" label={t('internal-service-management:markets_published')} />
          ) : (
            <Chip size="small" variant="outlined" label={t('internal-service-management:markets_not_published')} />
          ),
        sortable: true,
        sortAccessor: ({ row }) => (row.market.published ? 0 : 1),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 180 } },
      },
      rowActionsColumn<MarketRow, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-service-management:markets_edit_short'), onClick: () => customTableProps?.onEdit(row) },
          {
            icon: <Public />,
            label: row.market.published ? t('internal-service-management:markets_already_published') : t('internal-service-management:markets_publish'),
            ariaLabel: t('internal-service-management:markets_publish'),
            disabled: row.market.published,
            onClick: () => customTableProps?.onPublish(row),
          },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => setDialogState({ open: true, row }),
      onPublish: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:markets_publish'),
          confirmText: (
            <Stack spacing={2}>
              <Typography>{t('internal-service-management:markets_publish_confirm', { name: row.market.name })}</Typography>
              <Alert severity="warning">{t('internal-service-management:markets_publish_irreversible')}</Alert>
            </Stack>
          ),
          actionText: t('internal-service-management:markets_publish'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [t]
  )

  const handleSave = useCallback(
    async (market: MarketMutation) => {
      try {
        const saved = await saveMarket(market).unwrap()
        const unstored = getUnstoredMarketFields(market, saved)
        if (unstored.length) {
          const fields = unstored.map((field) => t(marketFieldLabelKeys[field])).join(', ')
          notifyError(t('internal-service-management:markets_not_stored', { fields }))
          return
        }
        notifySuccess(t('internal-service-management:markets_saved', { name: market.name }))
        setDialogState({ open: false })
      } catch {
        notifyError(t('internal-service-management:markets_save_failed'))
      }
    },
    [notifyError, notifySuccess, saveMarket, t]
  )

  const handleConfirmPublish = useCallback(
    async (confirmed?: ConfirmDialogData<MarketRow>) => {
      setConfirmDialogData(undefined)
      const row = confirmed?.data
      if (!row) return
      const published = await run(publishMarket(row.market.iso).unwrap(), { error: t('internal-service-management:markets_publish_failed') })
      if (!published.ok) return
      if (!published.result?.published) {
        notifyError(t('internal-service-management:markets_publish_not_stored', { name: row.market.name }))
        return
      }
      notifySuccess(t('internal-service-management:markets_published_message', { name: row.market.name }))
    },
    [notifyError, notifySuccess, publishMarket, run, t]
  )

  return (
    <Box>
      <InternalListToolbar
        add={{ label: t('internal-service-management:markets_add'), onClick: () => setDialogState({ open: true }), disabled: isLoading || isError }}
      />

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-service-management:markets_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => row.market.id}
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <MarketEditDialog
        open={dialogState.open}
        row={dialogState.row}
        markets={markets}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmPublish} />
    </Box>
  )
}
